// Verification of point-lookup inclusion proofs.
//
// A point lookup returns one entry instead of a signed file, so on its own it would
// be unbacked data — in precisely the mode where the client relies on the server
// most. The response therefore carries a hash path to a root, and the root carries
// the directory's k-of-n signatures.
//
// Concrete scheme (the contract a directory server must produce):
// * leaf = SHA-256 of the canonical JSON of the entry object, exactly as served;
// * path = sibling hashes from the leaf upwards, base64url;
// * the side on which the sibling is combined at depth i comes from bit i of the
//   ENTRY KEY (0 ⇒ running hash is the left input, 1 ⇒ the right), so the path
//   needs no left/right markers;
// * root = the final hash, and the response's signatures must clear the manifest's
//   threshold over the canonical form of {"root": …, "snapshot_version": …}.

import { b64uDecodeOrNull } from './base64url';
import { canonical } from './canonical-json';
import { sha256, utf8 } from './hash';
import { thresholdVerifyBody } from './threshold';

export interface EntryProofInput {
  /** The entry object exactly as served — hashing anything re-serialised breaks the leaf. */
  rawEntry: unknown;
  proofRootB64: string;
  proofPathB64: readonly string[];
  snapshotVersion: number;
  signatures: unknown;
}

/**
 * Whether [input] proves that its entry belongs to the signed snapshot. Any failure —
 * a broken path, a root that does not match, signatures below the threshold — makes
 * the whole lookup worthless, so the answer is a single yes or no.
 */
export async function verifyEntryProof(
  input: EntryProofInput,
  entryKeyBytes: Uint8Array,
  allowedKeys: ReadonlyMap<string, string>,
  threshold: number,
): Promise<boolean> {
  const root = decodeNonEmpty(input.proofRootB64);
  if (root === null) {
    return false;
  }

  let running = await sha256(utf8(canonical(input.rawEntry)));
  for (let depth = 0; depth < input.proofPathB64.length; depth++) {
    const sibling = decodeNonEmpty(input.proofPathB64[depth]!);
    if (sibling === null) {
      return false;
    }
    running =
      keyBit(entryKeyBytes, depth) === 0
        ? await sha256(concat(running, sibling))
        : await sha256(concat(sibling, running));
  }
  if (!constantTimeEquals(running, root)) {
    return false;
  }

  return thresholdVerifyBody(
    { root: input.proofRootB64, snapshot_version: input.snapshotVersion },
    input.signatures,
    allowedKeys,
    threshold,
  );
}

function keyBit(key: Uint8Array, index: number): number {
  const byte = Math.floor(index / 8);
  if (byte >= key.length) {
    return 0;
  }
  return (key[byte]! >> (7 - (index % 8))) & 1;
}

function decodeNonEmpty(b64: string): Uint8Array | null {
  if (b64.length === 0) {
    return null;
  }
  return b64uDecodeOrNull(b64);
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

function constantTimeEquals(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false;
  }
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a[i]! ^ b[i]!;
  }
  return diff === 0;
}
